import { z } from 'zod';
import { GitCrSummaryBasis } from './git-cr-summary.js';
/**
 * Đối chiếu tiêu chí của một việc với MR đã gắn — APM-GIT-n,
 * docs/plan-git-integration.md §8.5 (skill `review_task_coverage`).
 *
 * Tệp riêng (không nối vào `git.ts`) vì các task `APM-GIT-*` chạy song song cùng
 * thêm kiểu.
 *
 * Output là **gợi ý** (luật #6): nằm ở `ai_suggestion` (`pending`) như mọi skill
 * đi đường chung, hiện ở khối "Phát triển" của việc. Không sửa task, không đổi
 * trạng thái, không chặn merge — chỉ chỉ ra tiêu chí **chưa thấy** MR nào nhắc.
 */
export const REVIEW_TASK_COVERAGE_SKILL = 'review_task_coverage';
/** Trần số tiêu chí một lần đối chiếu — mô tả dài hơn thế là một tài liệu, không phải một việc. */
export const GIT_TASK_COVERAGE_CRITERIA_MAX = 30;
/** Trần chữ một tiêu chí / một câu trích. */
export const GIT_TASK_COVERAGE_TEXT_MAX = 500;
const Text = z.string().trim().min(1).max(GIT_TASK_COVERAGE_TEXT_MAX);
/**
 * Căn cứ cho một tiêu chí **đã thấy**: câu trích nguyên văn từ MR số `number`
 * (tiêu đề, mô tả, tiêu đề commit, hoặc diff khi kho bật đọc mã). `finalize()`
 * đối chiếu câu trích với kết quả tool của chính lượt chạy — không có ở đó thì
 * mục ấy thành "chưa kiểm được".
 */
export const GitTaskCoverageEvidence = z.object({
    number: z.number().int().positive(),
    quote: Text,
});
export const GitTaskCoverageCriterion = z.object({
    /** Chép **nguyên văn** từ mô tả của việc — `finalize()` loại tiêu chí không có trong đó. */
    criterion: Text,
    /** `true` = có MR nhắc tới (kèm `evidence`); `false` = chưa thấy MR nào nhắc. */
    seen: z.boolean(),
    evidence: GitTaskCoverageEvidence.nullable().default(null),
});
/** Một MR mà lượt chạy **đã đọc** — đặt bởi `finalize()` từ kết quả tool, không do model khai. */
export const GitTaskCoverageChecked = z.object({
    repositoryId: z.string(),
    repository: z.string(),
    number: z.number().int().positive(),
    title: z.string(),
});
/** Output của skill `review_task_coverage`. */
export const GitTaskCoverageOutput = z.object({
    /** Việc được đối chiếu — `finalize()` đặt từ tham số, để màn hình của việc chỉ hiện gợi ý của chính nó. */
    taskId: z.string().nullable().default(null),
    /** Rỗng = việc không có tiêu chí nào để đối chiếu (giao diện nói đúng như vậy). */
    criteria: z.array(GitTaskCoverageCriterion).max(GIT_TASK_COVERAGE_CRITERIA_MAX),
    /** Một câu kết luận cho người đọc. */
    summary: z.string().trim().min(1).max(600),
    /** Như `summarize_change_request`: `diff` chỉ khi lượt chạy thật sự đọc mã. Do `finalize()` đặt. */
    basedOn: GitCrSummaryBasis.default('description'),
    checked: z.array(GitTaskCoverageChecked).max(50).default([]),
});
// ── Đường làm tay: tiêu chí đọc từ mô tả ────────────────────────────────────
const CRITERIA_HEADING = /(tiêu chí|nghiệm thu|acceptance|definition of done|\bdod\b|điều kiện hoàn thành)/i;
const HEADING = /^\s{0,3}(#{1,6})\s+(.*)$/;
/** Dòng đầu mục danh sách: `-`, `*`, `+`, `1.`, `1)`, kể cả ô tick `[ ]` / `[x]`. */
const LIST_ITEM = /^\s*(?:[-*+]|\d{1,3}[.)])\s+(?:\[[ xX]\]\s+)?(.*\S)\s*$/;
/** Dòng tiêu đề kiểu "Tiêu chí nghiệm thu:" không có `#`. */
const LABEL_LINE = /^\s*\**([^*:]{3,80}?)\**\s*:\s*\**\s*$/;
/**
 * Tiêu chí của một việc đọc từ mô tả — bảng "tiêu chí cạnh danh sách MR" của
 * đường làm tay (`manual` của skill), và cũng là quy ước mà prompt của skill mô tả.
 *
 * - Có mục tiêu đề "Tiêu chí nghiệm thu" / "Acceptance criteria" / "DoD"
 *   (`## …` hoặc một dòng "…:") ⇒ các mục danh sách **trong mục đó**;
 * - không có ⇒ mọi mục danh sách của mô tả;
 * - không có danh sách nào ⇒ rỗng: không có gì để đối chiếu.
 *
 * Hàm thuần, chịu `null`. Trần `GIT_TASK_COVERAGE_CRITERIA_MAX` mục.
 */
export function extractTaskCriteria(description) {
    if (!description)
        return [];
    const lines = description.replace(/\r\n?/g, '\n').split('\n');
    let inSection = false;
    let sectionLevel = 0;
    let sawSection = false;
    const inside = [];
    const all = [];
    for (const line of lines) {
        const heading = HEADING.exec(line);
        const label = heading ? null : LABEL_LINE.exec(line);
        if (heading || label) {
            const level = heading ? heading[1].length : 7;
            const text = heading ? heading[2] : label[1];
            if (CRITERIA_HEADING.test(text)) {
                inSection = true;
                sawSection = true;
                sectionLevel = level;
                continue;
            }
            // Tiêu đề cùng cấp hoặc cao hơn đóng mục; dòng "nhãn:" đóng mục của một dòng "nhãn:".
            if (inSection && level <= sectionLevel)
                inSection = false;
            continue;
        }
        const item = LIST_ITEM.exec(line);
        if (!item)
            continue;
        const text = item[1].trim();
        if (text.length === 0)
            continue;
        all.push(text);
        if (inSection)
            inside.push(text);
    }
    const picked = sawSection && inside.length > 0 ? inside : all;
    return picked.slice(0, GIT_TASK_COVERAGE_CRITERIA_MAX);
}
//# sourceMappingURL=git-task-coverage.js.map